import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, ExternalLink, Loader2, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOTFORM_URLS = {
  sole_trader: "https://form.jotform.com/261137897561871",
  company: "https://form.jotform.com/252310661914855",
};

const ENTITY_LABEL = {
  sole_trader: "Sole Trader",
  company: "Company",
};

/**
 * Builds a prefilled Jotform URL by appending standard query params.
 * Jotform automatically prefills any matching field "Unique Name" with
 * a query string of the same key. The user can rename Jotform fields
 * to these keys (or whichever they prefer) to enable prefill.
 */
function buildJotformUrl(entity, lead) {
  const base = JOTFORM_URLS[entity];
  if (!base || !lead) return base || "";
  const params = new URLSearchParams();
  if (lead.name) params.set("name", lead.name);
  if (lead.email) params.set("email", lead.email);
  if (lead.phone) params.set("phone", lead.phone);
  if (lead.business_name) params.set("businessName", lead.business_name);
  if (lead.plan_id) params.set("plan", lead.plan_id);
  if (lead.entity) params.set("entity", lead.entity);
  if (lead.financial_year) params.set("financialYear", String(lead.financial_year));
  if (lead.prior_financial_year)
    params.set("priorFinancialYear", String(lead.prior_financial_year));
  if (lead.id) params.set("leadId", lead.id);
  if (lead.uses_xero !== undefined) params.set("usesXero", lead.uses_xero ? "Yes" : "No");
  if (lead.prior_year_catchup !== undefined)
    params.set("priorYearCatchup", lead.prior_year_catchup ? "Yes" : "No");
  return `${base}?${params.toString()}`;
}

export default function Onboarding() {
  const [params] = useSearchParams();
  const leadId = params.get("lead_id");
  const entityParam = params.get("entity");
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!leadId) {
      // Trial / preview mode — no lead, just embed the entity form without prefill.
      setLoading(false);
      if (!entityParam) {
        setError("Add ?entity=sole_trader or ?entity=company to preview the onboarding form.");
      }
      return;
    }
    axios
      .get(`${API}/leads/${leadId}`)
      .then((r) => setLead(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || e?.message || "Couldn't load your details.")
      )
      .finally(() => setLoading(false));
  }, [leadId, entityParam]);

  const entity = lead?.entity || entityParam;
  const jotformUrl = entity ? buildJotformUrl(entity, lead) : null;

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#0F172A]"
      data-testid="onboarding-page"
    >
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="onboarding-home-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_small-biz-tax-hub/artifacts/gg58om9x_NXTAXLOGOW.2022.png"
              alt="NZ Taxation Limited"
              className="h-9 w-9 object-contain"
            />
            <div className="leading-tight">
              <div className="font-heading font-black text-[15px]">NZ Taxation</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                Onboarding
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4 text-[#C9A349]" />
            Secure onboarding · NZ Taxation Limited
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
          data-testid="onboarding-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back to homepage
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <CheckCircle2 className="w-7 h-7 text-[#C9A349]" />
          <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Payment received. Let's get you set up.
          </h1>
        </div>
        {lead && (
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed" data-testid="onboarding-greeting">
            Hi <strong className="text-slate-900">{lead.name}</strong> — welcome aboard. Please
            complete the {ENTITY_LABEL[entity] || ""} onboarding form below so we can prepare
            your first monthly tax position email and connect your records.
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {entity && (
            <span
              className="inline-flex items-center px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold border border-slate-300 bg-white"
              data-testid="onboarding-entity-tag"
            >
              {ENTITY_LABEL[entity]} onboarding
            </span>
          )}
          {lead?.financial_year && (
            <span className="inline-flex items-center px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold bg-[#0A192F] text-white">
              Covering FY{lead.financial_year}
            </span>
          )}
          {lead?.prior_year_catchup && (
            <span className="inline-flex items-center px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold bg-[#C9A349] text-[#0A192F]">
              + FY{lead.prior_financial_year} catch-up
            </span>
          )}
        </div>

        <div className="mt-10 border border-slate-200 bg-white">
          {loading && (
            <div
              className="flex flex-col items-center justify-center gap-3 py-24"
              data-testid="onboarding-loading"
            >
              <Loader2 className="w-7 h-7 text-[#C9A349] animate-spin" />
              <p className="text-slate-600 text-sm">Preparing your onboarding form…</p>
            </div>
          )}
          {!loading && error && (
            <div className="px-6 py-12 text-center" data-testid="onboarding-error">
              <h2 className="font-heading font-bold text-2xl text-slate-900">
                We couldn't load your form
              </h2>
              <p className="mt-2 text-slate-600">{error}</p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 font-medium hover:bg-[#C9A349] transition-colors"
              >
                Back to homepage
              </Link>
            </div>
          )}
          {!loading && !error && jotformUrl && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-3 border-b border-slate-200 bg-slate-50">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
                  Step 2 of 2 · Complete your onboarding
                </div>
                <a
                  href={jotformUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-[#C9A349] transition-colors"
                  data-testid="onboarding-open-new-tab"
                >
                  Open in new tab <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <iframe
                ref={iframeRef}
                src={jotformUrl}
                title={`${ENTITY_LABEL[entity]} onboarding form`}
                className="w-full"
                style={{ minHeight: "1400px", border: "0" }}
                allow="geolocation; microphone; camera; fullscreen"
                allowFullScreen
                data-testid="onboarding-iframe"
              />
            </>
          )}
          {!loading && !error && !jotformUrl && (
            <div className="px-6 py-12 text-center text-slate-600" data-testid="onboarding-no-entity">
              We couldn't determine which onboarding form to load. Please return to the homepage
              and contact info@nztaxation.nz.
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-slate-500 max-w-3xl">
          Trouble with the embedded form? You can also{" "}
          {jotformUrl ? (
            <a
              href={jotformUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[#C9A349]"
            >
              open it in a new tab
            </a>
          ) : (
            "use your direct link from the welcome email"
          )}{" "}
          or email us at{" "}
          <a href="mailto:info@nztaxation.nz" className="underline hover:text-[#C9A349]">
            info@nztaxation.nz
          </a>
          .
        </p>
      </section>
    </main>
  );
}
